import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { jsonStorage } from './persist';
import { setAnalyticsConsent } from '../lib/analytics';
import { sound } from '../lib/sound';

export interface SettingsState {
  soundEnabled: boolean;
  soundVolume: number;
  musicEnabled: boolean;
  animationsEnabled: boolean;
  hideBalances: boolean;
  reducedMotion: boolean;
  analyticsConsent: boolean;
  defaultBet: number;
  quickBets: number[];
  confirmLargeBets: boolean;
  largeBetThreshold: number;
  setSoundEnabled: (v: boolean) => void;
  setSoundVolume: (v: number) => void;
  setMusicEnabled: (v: boolean) => void;
  setAnimationsEnabled: (v: boolean) => void;
  setHideBalances: (v: boolean) => void;
  setReducedMotion: (v: boolean) => void;
  setAnalyticsConsent: (v: boolean) => void;
  setDefaultBet: (v: number) => void;
  setQuickBets: (v: number[]) => void;
  setConfirmLargeBets: (v: boolean) => void;
}

export const useSettings = create<SettingsState>()(
  persist(
    (set) => ({
      soundEnabled: true,
      soundVolume: 0.7,
      musicEnabled: false,
      animationsEnabled: true,
      hideBalances: false,
      reducedMotion: false,
      analyticsConsent: false,
      defaultBet: 10,
      quickBets: [1, 5, 10, 25, 50, 100],
      confirmLargeBets: true,
      largeBetThreshold: 500,

      setSoundEnabled: (v) => {
        sound.setEnabled(v);
        set({ soundEnabled: v });
      },
      setSoundVolume: (v) => {
        sound.setVolume(v);
        set({ soundVolume: v });
      },
      setMusicEnabled: (musicEnabled) => set({ musicEnabled }),
      setAnimationsEnabled: (animationsEnabled) => set({ animationsEnabled }),
      setHideBalances: (hideBalances) => set({ hideBalances }),
      setReducedMotion: (reducedMotion) => set({ reducedMotion }),
      setAnalyticsConsent: (v) => {
        setAnalyticsConsent(v);
        set({ analyticsConsent: v });
      },
      setDefaultBet: (defaultBet) => set({ defaultBet }),
      setQuickBets: (quickBets) => set({ quickBets }),
      setConfirmLargeBets: (confirmLargeBets) => set({ confirmLargeBets }),
    }),
    {
      name: 'settings',
      storage: jsonStorage,
      onRehydrateStorage: () => (state) => {
        if (state) {
          sound.setEnabled(state.soundEnabled);
          sound.setVolume(state.soundVolume);
          setAnalyticsConsent(state.analyticsConsent);
        }
      },
    },
  ),
);

/** Respect the OS-level motion preference unless the user overrides it. */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
